/**
 * Nova solicitação dentro da mesa: catálogo na coluna da carteira
 * e abertura do processo no centro, no lugar da execução.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useOrganizationId } from '../../contexts/WorkspaceContext';
import { useExecutionSelection } from '../../contexts/ExecutionSelectionContext';
import { useAppToast } from '../AppToast';
import { EntryFlowService, type EntryFlow } from '../../services/entryFlowService';
import { createExecutionInstance } from '../../services/executionService';
import { CatalogPhase, FormPhase, categoryToken } from './LauncherDrawer';

type Priority = 'baixa' | 'media' | 'alta' | 'critica';

function useEntryCatalog(enabled: boolean) {
  const orgId = useOrganizationId();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const { data: flows = [], isLoading } = useQuery({
    queryKey: ['entry-flows', orgId, user?.role],
    queryFn: () => EntryFlowService.getEntryFlows(orgId, user?.role),
    enabled: enabled && !!orgId,
    staleTime: 60_000,
  });

  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('dataflow-launcher-favorites');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  const toggleFavorite = useCallback((flowId: string) => {
    setFavoriteIds((prev) => {
      const next = prev.includes(flowId) ? prev.filter((id) => id !== flowId) : [...prev, flowId];
      localStorage.setItem('dataflow-launcher-favorites', JSON.stringify(next));
      return next;
    });
  }, []);

  const filteredFlows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return flows;
    return flows.filter((flow) =>
      flow.entryLabel.toLowerCase().includes(query)
      || flow.name.toLowerCase().includes(query)
      || (flow.entryDescription?.toLowerCase().includes(query) ?? false)
      || (flow.category?.toLowerCase().includes(query) ?? false)
      || flow.entryRoles.some((role) => role.toLowerCase().includes(query)),
    );
  }, [flows, search]);

  const favoriteFlows = useMemo(
    () => filteredFlows.filter((flow) => favoriteIds.includes(flow.id)),
    [filteredFlows, favoriteIds],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, EntryFlow[]>();
    for (const flow of filteredFlows) {
      const key = (flow.category || 'geral').toLowerCase();
      const list = map.get(key) ?? [];
      list.push(flow);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort(([left], [right]) => left.localeCompare(right));
  }, [filteredFlows]);

  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const flow of flows) {
      const key = (flow.category || 'geral').toLowerCase();
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [flows]);

  return {
    search,
    setSearch,
    grouped,
    favoriteFlows,
    favoriteIds,
    toggleFavorite,
    categoryCounts,
    isLoading,
  };
}

export const RequestCatalogPanel: React.FC<{
  onSelect: (flow: EntryFlow) => void;
}> = ({ onSelect }) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const catalog = useEntryCatalog(true);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <CatalogPhase
        searchRef={searchRef}
        searchValue={catalog.search}
        onSearchChange={catalog.setSearch}
        grouped={catalog.grouped}
        favoriteFlows={catalog.favoriteFlows}
        favoriteIds={catalog.favoriteIds}
        onToggleFavorite={catalog.toggleFavorite}
        categoryCounts={catalog.categoryCounts}
        isLoading={catalog.isLoading}
        onSelectFlow={onSelect}
      />
    </div>
  );
};

export const RequestOpenStage: React.FC<{
  flow: EntryFlow;
  onCancel: () => void;
  onOpened: () => void;
}> = ({ flow, onCancel, onOpened }) => {
  const orgId = useOrganizationId();
  const { user } = useAuth();
  const { showToast } = useAppToast();
  const queryClient = useQueryClient();
  const { selectTask, selectInstance } = useExecutionSelection();
  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState(() => {
    const day = String(new Date().getDate()).padStart(2, '0');
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    return `${flow.entryLabel} — ${day}/${month}`;
  });
  const [priority, setPriority] = useState<Priority>('media');
  const [creating, setCreating] = useState(false);

  const handleCreate = useCallback(async () => {
    if (!title.trim() || creating) return;
    setCreating(true);
    try {
      const created = await createExecutionInstance({
        organizationId: orgId,
        blueprintId: flow.id,
        versionId: flow.latestVersionId ?? undefined,
        title: title.trim(),
        priority,
        initiatedBy: user?.id,
        contextData: {},
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['minha-mesa'] }),
        queryClient.invalidateQueries({ queryKey: ['execution'] }),
        queryClient.invalidateQueries({ queryKey: ['entry-flows'] }),
      ]);
      if (created.firstTaskId) {
        selectInstance(created.id);
        selectTask(created.firstTaskId);
        try { localStorage.setItem('workflow.minha-mesa.viewMode', 'list'); } catch { /* noop */ }
        showToast('success', `Solicitação "${title.trim()}" aberta na primeira etapa`);
      } else {
        showToast('success', `Solicitação "${title.trim()}" criada com sucesso`);
      }
      onOpened();
    } catch (error: unknown) {
      showToast('error', error instanceof Error ? error.message : 'Erro ao criar solicitação');
    } finally {
      setCreating(false);
    }
  }, [title, creating, orgId, flow.id, flow.latestVersionId, priority, user?.id, queryClient, selectInstance, selectTask, showToast, onOpened]);

  return (
    <div
      className="flex min-h-0 flex-1 flex-col bg-[var(--bg-workspace)]"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && title.trim()) {
          event.preventDefault();
          void handleCreate();
        }
      }}
    >
      <div className="border-b border-slate-200/80 bg-white dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)]">
        <FormPhase
          variant="sheet"
          titleRef={titleRef}
          flow={flow}
          categoryToken={categoryToken(flow.category)}
          title={title}
          onTitleChange={setTitle}
          priority={priority}
          onPriorityChange={setPriority}
          creating={creating}
          onSubmit={() => { void handleCreate(); }}
          onCancel={onCancel}
        />
      </div>
    </div>
  );
};
